'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Region {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  highlights: string[];
  bestFor: string;
  depthRange: string;
  season: string;
  ctaLabel: string;
  ctaHref: string;
  ctaSupporting: string;
}

const regions: Region[] = [
  {
    id: 'larnaca',
    slug: 'larnaca',
    name: 'Larnaca',
    tagline: 'Home of the Zenobia — one of the world\'s top wreck dives',
    description:
      'Larnaca is Cyprus\'s most celebrated diving region, anchored by the legendary MS Zenobia — a 172m ferry resting on her side at 16–42m, packed with trucks and teeming with marine life. Beyond the Zenobia, the region offers multiple wrecks, the unique Pyla Pirate Caves where you can surface inside a mountain, and shallow reefs ideal for beginners. Whether you\'re an Open Water diver or a technical specialist, Larnaca has a dive for you.',
    image: '/assets/images/larnaca-cyprus-coastline-finikingoudes-beach-diving-1777208653179.jpg',
    alt: 'Larnaca coastline at Finikoudes Beach, Cyprus diving region',
    highlights: ['MS Zenobia wreck (world top 10)', 'HMS Cricket & Elpida wrecks', 'Pyla Pirate Caves', 'LEF1 reef for beginners', 'Shore diving at Bullet Point & Nail'],
    bestFor: 'Wreck diving, all levels',
    depthRange: '3–42m',
    season: 'Year-round',
    ctaLabel: 'Browse Larnaca dive sites',
    ctaHref: '/dive-sites/larnaca',
    ctaSupporting: 'See listed Larnaca dive sites with filters for type, access, depth and experience level.',
  },
  {
    id: 'limassol',
    slug: 'limassol',
    name: 'Limassol',
    tagline: 'Twin wrecks, artificial reefs, and a dramatic mountain wall',
    description:
      'Limassol offers some of Cyprus\'s most dramatic underwater topography. The twin wrecks — Costandis and Lady Thetis — lie just 200m apart and are excellent for scooter diving. The Jubilee Shoals mountain wall drops to 65m and hosts shoals of amberjack, crayfish, and multiple caves at varying depths, making it a favourite for technical divers. Artificial reef structures added in 2016–2017 have quickly developed rich marine life and frequent turtle sightings.',
    image: '/assets/images/limassol-cyprus-scuba-diving-coastline-akrotiri-1773863481063.jpg',
    alt: 'Scuba diver exploring the coastline near Limassol, Cyprus',
    highlights: ['Costandis & Lady Thetis twin wrecks', 'Jubilee Shoals mountain wall (to 65m)', 'The Structures artificial reef', 'Technical diving opportunities', 'Turtle sightings year-round'],
    bestFor: 'Intermediate to technical divers',
    depthRange: '10–65m',
    season: 'April–October (wrecks year-round)',
    ctaLabel: 'Browse Limassol dive sites',
    ctaHref: '/dive-sites/limassol',
    ctaSupporting: 'See listed Limassol dive sites with filters for type, access, depth and experience level.',
  },
  {
    id: 'paphos',
    slug: 'paphos',
    name: 'Paphos',
    tagline: 'Ancient history meets underwater adventure',
    description:
      'Paphos blends archaeological intrigue with natural beauty. The Amphora Cave — a small cavern sheltering an ancient clay amphora — is unlike anything else in Cyprus. Multiple wrecks including the Achilleas (1949) and Vera K (1972) lie in shallow water, perfect for beginners. Rocky reefs, the Roman Wall artificial reef, and the Pistol Cave shore dive round out a region that rewards both casual snorkellers and experienced divers.',
    image: '/assets/images/paphos-cyprus-harbour-diving-1777010591520.jpg',
    alt: 'Paphos harbour and coastline, Cyprus diving region',
    highlights: ['Amphora Cave — unique archaeological site', 'Achilleas & Vera K shallow wrecks', 'Pistol Cave shore dive', 'Roman Wall artificial reef', 'Diverse rocky reefs'],
    bestFor: 'Beginners, history enthusiasts',
    depthRange: '2–21m',
    season: 'Year-round',
    ctaLabel: 'Browse Paphos dive sites',
    ctaHref: '/dive-sites/paphos',
    ctaSupporting: 'See listed Paphos dive sites with filters for type, access, depth and experience level.',
  },
  {
    id: 'ayia-napa-protaras',
    slug: 'ayia-napa-protaras',
    name: 'Ayia Napa & Protaras',
    tagline: 'Crystal-clear waters, easy shore entries, and frequent turtle sightings',
    description:
      'The east coast around Ayia Napa and Protaras is the most beginner-friendly diving area in Cyprus. Walk-in shore entries at Green Bay and Da Costa make it ideal for introductory dives. Underwater statues at 10m, beautiful caverns at Green Bay Cave, and the dramatic Cyclopes Caves drop-off provide variety for all levels. Turtles are a regular sighting, and the twin wrecks Nemesis III and Liberty lie just 90m apart — perfect for a scooter dive double.',
    image: '/assets/images/ayia-napa-protaras-diving-lovers-bridge-cyprus-1774173010310.jpg',
    alt: 'Lovers Bridge coastline near Ayia Napa and Protaras, Cyprus',
    highlights: ['Green Bay — easiest entry in Cyprus', 'Underwater statues at 10m', 'Cyclopes Caves dramatic drop-off', 'Nemesis III & Liberty twin wrecks', 'Frequent green sea turtle sightings'],
    bestFor: 'Beginners, families, turtle spotting',
    depthRange: '3–45m',
    season: 'Year-round',
    ctaLabel: 'Browse Ayia Napa & Protaras dive sites',
    ctaHref: '/dive-sites/ayia-napa-protaras',
    ctaSupporting: 'See listed Ayia Napa & Protaras dive sites with filters for type, access, depth and experience level.',
  },
  {
    id: 'akamas-latchi',
    slug: 'akamas-latchi',
    name: 'Akamas & Latchi',
    tagline: 'Pristine wilderness diving with dramatic drop-offs and abundant marine life',
    description:
      'The Akamas Peninsula and Latchi area is Cyprus\'s most unspoiled diving destination. Protected national park status means the marine environment is exceptionally rich — shoals of amberjack, crayfish, groupers, and stingrays are common. St. Georgios Island offers a dramatic wall dropping to 55m with caves at multiple depths. The Blue Lagoon provides a relaxed beginner option, while Mazaki Island — barely breaking the surface — is one of the best marine life sites in Cyprus for experienced divers willing to handle the currents.',
    image: '/assets/images/green-bay-cyprus-underwater-protaras-diving-1777197327475.jpg',
    alt: 'Crystal clear waters of the Akamas coastline, Cyprus',
    highlights: ['St. Georgios Island wall (to 55m)', 'Blue Lagoon — relaxed beginner dive', 'Mazaki Island — exceptional marine life', 'Photiades Wall drift dive', 'Protected national park waters'],
    bestFor: 'Intermediate to advanced, marine life enthusiasts',
    depthRange: '3–55m',
    season: 'May–September (some sites year-round)',
    ctaLabel: 'Browse Akamas & Latchi dive sites',
    ctaHref: '/dive-sites/akamas-latchi',
    ctaSupporting: 'See listed Akamas & Latchi dive sites with filters for type, access, depth and experience level.',
  },
];

const RegionalDiveSitesInteractive = () => {
  return (
    <div className="min-h-screen bg-background">

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #071a24 0%, #0E1C2B 60%, #0a1e2e 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #6FD3E0 0%, transparent 60%), radial-gradient(circle at 70% 20%, #FF8A3D 0%, transparent 50%)' }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6" style={{ background: 'rgba(111,211,224,0.12)', color: '#6FD3E0', border: '1px solid rgba(111,211,224,0.25)' }}>
              <Icon name="MapPinIcon" size={16} />
              <span>5 Diving Regions · Cyprus</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-headline font-bold mb-5" style={{ color: '#EAF4F8' }}>
              Cyprus Diving Regions Guide
            </h1>
            <p className="text-lg mb-8 leading-relaxed" style={{ color: '#C8DDE6' }}>
              Cyprus has five distinct diving regions, each with its own character, conditions, and signature sites. Use this guide to understand what each region offers — then head to the dive site directory to browse and filter specific sites.
            </p>
            <Link
              href="/dive-sites"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:opacity-90"
              style={{ background: '#6FD3E0', color: '#0B1622' }}
            >
              <Icon name="MagnifyingGlassIcon" size={18} />
              Browse the Full Dive Site Directory
            </Link>
          </div>
        </div>
      </section>

      {/* Region Guide Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-20">
            {regions.map((region, index) => (
              <div key={region.id} className="scroll-mt-24" id={region.slug}>

                {/* Region Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: 'rgba(111,211,224,0.15)', color: '#6FD3E0', border: '1.5px solid rgba(111,211,224,0.3)' }}>
                    {index + 1}
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-headline font-bold text-foreground mb-1">
                      {region.name}
                    </h2>
                    <p className="text-base font-medium" style={{ color: '#6FD3E0' }}>{region.tagline}</p>
                  </div>
                </div>

                {/* Region Image */}
                <div className="rounded-2xl overflow-hidden mb-6 aspect-[16/7]">
                  <img
                    src={region.image}
                    alt={region.alt}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Description */}
                <p className="text-base leading-relaxed text-muted-foreground mb-6">
                  {region.description}
                </p>

                {/* Quick Facts + Highlights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Highlights */}
                  <div className="rounded-xl p-5" style={{ background: 'rgba(111,211,224,0.06)', border: '1px solid rgba(111,211,224,0.15)' }}>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#6FD3E0' }}>
                      Highlights
                    </h3>
                    <ul className="space-y-2">
                      {region.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                          <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(111,211,224,0.2)' }}>
                            <Icon name="CheckIcon" size={10} className="text-primary" />
                          </span>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Quick Facts */}
                  <div className="rounded-xl p-5" style={{ background: 'rgba(255,138,61,0.06)', border: '1px solid rgba(255,138,61,0.15)' }}>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#FF8A3D' }}>
                      At a Glance
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm">
                        <Icon name="UserGroupIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground">Best for:</span>
                        <span className="text-foreground font-medium">{region.bestFor}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Icon name="ArrowsUpDownIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground">Depth range:</span>
                        <span className="text-foreground font-medium">{region.depthRange}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Icon name="CalendarDaysIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground">Season:</span>
                        <span className="text-foreground font-medium">{region.season}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Block */}
                <div className="rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ background: 'linear-gradient(135deg, #0E1C2B 0%, #071a24 100%)', border: '1px solid rgba(111,211,224,0.2)' }}>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{region.ctaSupporting}</p>
                  </div>
                  <Link
                    href={region.ctaHref}
                    className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:opacity-90 whitespace-nowrap"
                    style={{ background: '#6FD3E0', color: '#0B1622' }}
                  >
                    {region.ctaLabel}
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                </div>

                {/* Divider (not after last) */}
                {index < regions.length - 1 && (
                  <div className="mt-20 border-t border-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #0E1C2B 0%, #071a24 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-headline font-bold mb-4" style={{ color: '#EAF4F8' }}>
              Ready to find your next dive?
            </h2>
            <p className="text-base mb-8" style={{ color: '#C8DDE6' }}>
              The full dive site directory lets you search and filter every site in Cyprus by region, depth, access type, and experience level.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href="/dive-sites"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-200 hover:opacity-90"
                style={{ background: '#6FD3E0', color: '#0B1622' }}
              >
                <Icon name="MagnifyingGlassIcon" size={20} />
                Browse All Dive Sites
              </Link>
              <Link
                href="/dive-centres-directory"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-200 hover:bg-cyan-400/10"
                style={{ background: 'transparent', color: '#6FD3E0', border: '1.5px solid #6FD3E0' }}
              >
                <Icon name="BuildingStorefrontIcon" size={20} />
                Find Dive Centres
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default RegionalDiveSitesInteractive;